/**
 * VersionCompareView.tsx — "what changed between two versions" (story
 * E0-BO-CFG-SKILL-REGISTRY-ADMIN, "Compare").
 *
 * Fetches both selected versions' details itself (two useSkillVersionDetail
 * instances) and renders a path/before/after table via promptTemplateDiff —
 * a plain side-by-side of the changed fields, no diff library.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import { diffSkillVersions, type VersionDiffRow } from './promptTemplateDiff';
import { useSkillVersionDetail, type SkillVersionDetailParams } from './useSkillVersionDetail';

/**
 * ADR-084 D6 / design item 9: for agentic rows, group diff rows by the
 * editor tab they came from. `scripts`/`script` (legacy singular, ADR-082)
 * both land in the "scripts" group — S3b will re-key that group on
 * `step_id` once the step editor exists. Non-agentic rows keep the flat,
 * alphabetically-sorted table (unchanged from before this grouping existed).
 */
const TAB_GROUPS = [
  'persona',
  'boundaries',
  'cadence',
  'scripts',
  'facts',
  'eligibility',
  'other',
] as const;
type TabGroup = (typeof TAB_GROUPS)[number];

const groupForPath = (path: string): TabGroup => {
  if (path.startsWith('agent_config.definition.persona.')) return 'persona';
  if (path.startsWith('agent_config.boundaries')) return 'boundaries';
  if (path === 'agent_config.cadence_instructions') return 'cadence';
  if (path.startsWith('agent_config.scripts.') || path.startsWith('agent_config.script.'))
    return 'scripts';
  if (path.startsWith('agent_config.facts.')) return 'facts';
  if (
    path.startsWith('agent_config.eligibility.') ||
    path.startsWith('agent_config.document_checklist.')
  )
    return 'eligibility';
  return 'other';
};

const groupRows = (rows: VersionDiffRow[]): Map<TabGroup, VersionDiffRow[]> => {
  const grouped = new Map<TabGroup, VersionDiffRow[]>();
  rows.forEach((row) => {
    const group = groupForPath(row.path);
    const existing = grouped.get(group);
    if (existing) existing.push(row);
    else grouped.set(group, [row]);
  });
  return grouped;
};

export interface VersionCompareViewProps {
  apiClient: ApiClient;
  left: SkillVersionDetailParams;
  right: SkillVersionDetailParams;
}

export const VersionCompareView = ({ apiClient, left, right }: VersionCompareViewProps) => {
  const { t } = useTranslation();
  const leftState = useSkillVersionDetail(apiClient, left);
  const rightState = useSkillVersionDetail(apiClient, right);

  if (leftState.phase === 'unauthorized' || rightState.phase === 'unauthorized') {
    return (
      <p
        role="alert"
        data-testid="config-compare-unauthorized"
        className="text-body text-status-down"
      >
        {t('config.errors.unauthorized')}
      </p>
    );
  }

  if (leftState.phase === 'loading' || rightState.phase === 'loading') {
    return (
      <p role="status" data-testid="config-compare-loading" className="text-body text-ink-2">
        {t('config.compare.loading')}
      </p>
    );
  }

  if (leftState.detail == null || rightState.detail == null) {
    return (
      <p role="alert" data-testid="config-compare-error" className="text-body text-status-down">
        {t('config.compare.error')}
      </p>
    );
  }

  const rows = diffSkillVersions(leftState.detail, rightState.detail);
  const isAgentic =
    leftState.detail.agent_config_json != null || rightState.detail.agent_config_json != null;
  const grouped = isAgentic ? groupRows(rows) : null;

  const groupLabel: Record<TabGroup, string> = {
    persona: t('config.editAgentic.tabs.persona'),
    boundaries: t('config.editAgentic.tabs.boundaries'),
    cadence: t('config.editAgentic.tabs.cadence'),
    scripts: t('config.editAgentic.tabs.scripts'),
    facts: t('config.editAgentic.tabs.facts'),
    eligibility: t('config.editAgentic.tabs.eligibility'),
    other: t('config.compare.otherGroup'),
  };

  return (
    <section aria-labelledby="config-compare-heading" className="flex flex-col gap-3">
      <h2 id="config-compare-heading" className="text-h2 text-ink-1">
        {t('config.compare.heading', {
          from: left.configVersion,
          to: right.configVersion,
        })}
      </h2>
      {rows.length === 0 ? (
        <p data-testid="config-compare-empty" className="text-body text-ink-2">
          {t('config.compare.noDifferences')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-ink-3/10">
          <table
            className="w-full border-collapse text-left text-body"
            data-testid="config-compare-table"
          >
            <caption className="sr-only">{t('config.compare.tableLabel')}</caption>
            <thead className="text-meta text-ink-2">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('config.compare.columns.field')}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('config.compare.columns.before', { version: left.configVersion })}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t('config.compare.columns.after', { version: right.configVersion })}
                </th>
              </tr>
            </thead>
            <tbody>
              {grouped != null
                ? TAB_GROUPS.filter((group) => (grouped.get(group)?.length ?? 0) > 0).map(
                    (group) => (
                      <Fragment key={group}>
                        <tr>
                          <th
                            scope="colgroup"
                            colSpan={3}
                            data-testid={`config-compare-group-${group}`}
                            className="bg-surface-1 px-4 py-1.5 text-left text-meta font-medium uppercase tracking-wide text-ink-3"
                          >
                            {groupLabel[group]}
                          </th>
                        </tr>
                        {(grouped.get(group) ?? []).map((row) => (
                          <tr
                            key={row.path}
                            data-testid="config-compare-row"
                            className="border-t border-outline align-top"
                          >
                            <td className="px-4 py-2 font-mono text-meta text-ink-2">{row.path}</td>
                            <td className="whitespace-pre-wrap px-4 py-2 text-ink-1">
                              {row.before ?? t('config.compare.absent')}
                            </td>
                            <td className="whitespace-pre-wrap px-4 py-2 text-ink-1">
                              {row.after ?? t('config.compare.absent')}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ),
                  )
                : rows.map((row) => (
                    <tr
                      key={row.path}
                      data-testid="config-compare-row"
                      className="border-t border-outline align-top"
                    >
                      <td className="px-4 py-2 font-mono text-meta text-ink-2">{row.path}</td>
                      <td className="whitespace-pre-wrap px-4 py-2 text-ink-1">
                        {row.before ?? t('config.compare.absent')}
                      </td>
                      <td className="whitespace-pre-wrap px-4 py-2 text-ink-1">
                        {row.after ?? t('config.compare.absent')}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
